import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { ApiClientError } from '../../services/apiClient'

export function LoginPage() {
  const { state, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (state.status === 'authenticated') {
    const fallback = state.user.audience === 'admin' ? '/admin' : '/dashboard'
    return <Navigate to={fallback} replace />
  }

  const from = (location.state as { from?: string } | null)?.from

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
      const target = from && from !== '/login' ? from : '/dashboard'
      navigate(target, { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.apiError.code === 'account_locked') {
          setError('Cuenta bloqueada temporalmente por intentos fallidos. Intenta de nuevo en unos minutos.')
        } else if (err.apiError.code === 'account_inactive') {
          setError('Tu cuenta aún no ha sido verificada. Revisa tu correo o registra de nuevo.')
        } else {
          setError(err.apiError.message)
        }
      } else {
        setError('No se pudo iniciar sesión. Verifica tu conexión e inténtalo de nuevo.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell title="Iniciar sesión" subtitle="Accede a tu cuenta Velt Intrepid">
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label className="field">
          <span>Correo electrónico</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={submitting}
          />
        </label>

        <label className="field">
          <span>Contraseña</span>
          <div className="password-row">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={submitting}
            />
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
        </label>

        {error && <p className="error">{error}</p>}

        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? 'Iniciando…' : 'Iniciar sesión'}
        </button>

        <div className="auth-links">
          <Link to="/register">Crear cuenta nueva</Link>
          <Link to="/password-reset">¿Olvidaste tu contraseña?</Link>
        </div>
      </form>
    </AuthShell>
  )
}

// ─── Shell visual compartido por las páginas de auth ──────────────────────
export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <h1>
            Velt <span className="accent">Intrepid</span>
          </h1>
          <h2>{title}</h2>
          {subtitle && <p className="auth-subtitle">{subtitle}</p>}
        </header>
        {children}
      </div>
    </div>
  )
}
